import React from 'react';
import { connect } from 'react-redux';
import { submit as submitForm, formValueSelector, getFormValues } from 'redux-form';
import { getCountryCallingCode, parsePhoneNumber } from 'react-phone-number-input';
import { setError, clearError } from '@/actions';
import {
  selectPaymentOption,
  toggleWantsInsurance,
  resetInstallmentsPlan,
  updatePurchaseFields,
  resetPaymentCardError,
  selectPaymentMethod,
  toggleWantsCarbonOffset,
  saveBulkPassengers,
  setPassengers,
  fetchYunoData,
} from '@/actions/purchase';
import { getWalletTypeForPurchase } from 'utils/loyalty';
import { getStoredPassengers } from 'utils/storedPassengers';
import selectIsPurchaserFormValid from 'utils/purchase/selectIsPurchaserFormValid';
import {
  cardPaymentAttempt,
  paymentAttempt,
  transferPaymentAttempt,
  thirdPartyPaymentAttempt,
  storePaymentAttempt,
  efectyPaymentAttempt,
  evertecPaymentAttempt,
  clearOTP,
  generateOTP,
  confirmEvertecPayment,
  yapePaymentAttempt,
  yunoPaymentAttempt,
  bnplPaymentAttempt,
  redeemModalToggle,
  walletPaymentAttempt,
} from '@/actions/payment';
import { paypalPaymentAttempt } from '@/actions/paypal';
import { fetchPaymentCards } from '@/actions/cards';
import availablePaymentEngines from 'constants/PaymentEngine';
import Checkout from './Checkout';
import { getBrand2ProcessorMap } from '../../../utils/documentMaps';
import { CieloTDCProvider } from '../../cielo/CieloTDCContext';
import { useReserhubAccount } from '../../../context/ReserhubAccount';

const mapStateToProps = (state) => {
  const {
    purchase,
    payment,
    siemprePlus,
    costapass,
    cards,
    whitelabelConfig: { features, env },
    reserhubAccount,
  } = state;

  const VALIDATE_PURCHASER = features.PURCHASER_FORM_ON === 'CHECKOUT';
  const CHECK_DOCUMENT = features.IDENTIFICATION_DOCUMENT_FOR_PURCHASER;

  const keys = [
    'purchaserFirstName',
    'purchaserLastName',
    features.SHOW_PURCHASER_SECOND_LAST_NAME_FIELD && 'purchaserSecondLastName',
    'email',
    'phoneCountry',
    'phone',
    'phoneCode',
    CHECK_DOCUMENT && 'documentType',
    CHECK_DOCUMENT && 'documentId',
    'taxpayerId',
    'taxpayerLegalName',
    'taxpayerAddress',
    'addressAttributes',
    features.SHOW_PROMOTIONAL_TERMS_CHECKBOX && 'promotionalTerms',
  ].filter(Boolean);

  const purchaseJs = purchase.toJS();
  const cardsJs = cards.toJS();
  const userData = siemprePlus.toJS().user;

  const cardList = cards.get('cards');
  const selectedCardReference = cards.get('selectedCard');

  const selectedCard = cardList.find(
    (card) => card.recurringDetail.recurringDetailReference === selectedCardReference,
  );

  const cardSelector = formValueSelector('card');
  const purchaserFields = formValueSelector('purchaser')(state, ...keys);
  const passengersFormValues = getFormValues('passengers')(state);
  const storedFirstPassenger = getStoredPassengers(features)[0];
  // Passengers form is destroyed on route change; persisted form values keep full PII for analytics.
  const firstPassengerProfile =
    passengersFormValues?.passengers?.[0] ||
    storedFirstPassenger ||
    purchaseJs.passengers?.[0] ||
    null;
  const legalTerms = formValueSelector('purchaser')(state, 'legalTerms');
  const requiresTermsValidation =
    features.POLICIES_DISCLAIMER_LOCATION === 'CHECKOUT' && features.SHOW_LEGAL_TERMS_CHECKBOX;

  if (purchaserFields.phone) {
    purchaserFields.phone = purchaserFields.phone.replace(/ /g, '');
  }

  const purchaserUpdated = keys.every((key) => {
    if (purchaserFields[key] === undefined) return true;
    // phoneCode is derived from phoneCountry; compare only the source field
    if (key === 'phoneCode') return true;
    if (key === 'email' && !features.SHOW_PURCHASER_EMAIL_FIELD) return true;
    if (key === 'phone') {
      const phoneCode =
        purchaserFields.phoneCountry && getCountryCallingCode(purchaserFields.phoneCountry);
      const parsedNumber = parsePhoneNumber(`+${phoneCode}${purchaserFields.phone}`);
      const phone = parsedNumber && parsedNumber.nationalNumber;
      return purchaseJs[key] === phone;
    }

    if (['taxpayerLegalName', 'taxpayerAddress'].includes(key)) {
      return purchaseJs[key] === undefined || purchaseJs[key] === purchaserFields[key];
    }

    if (key === 'promotionalTerms') {
      const stored = purchaseJs.meta?.accepted_promotional_terms;
      const formChecked = Boolean(purchaserFields.promotionalTerms);
      return (stored === 'true') === formChecked;
    }

    if (key === 'addressAttributes') {
      return Object.keys(purchaserFields[key]).every((field) => {
        return purchaseJs[key]?.[field] === purchaserFields[key][field];
      });
    }

    return purchaseJs[key] === purchaserFields[key];
  });

  const isPurchaserValid = purchaserUpdated && (!requiresTermsValidation || legalTerms);
  // Registration-independent validity (see selectIsPurchaserFormValid); used to gate wallet sheets.
  // Gated on VALIDATE_PURCHASER so it cannot report `false` (and block wallets) when purchaser
  // validation is bypassed for the tenant, matching `purchaserUpdated` below.
  const isPurchaserFormValid = VALIDATE_PURCHASER ? selectIsPurchaserFormValid(state) : true;

  const { availableWallets = [] } = purchaseJs;
  const selectedLoyaltyProgram = purchaseJs.walletType || getWalletTypeForPurchase();
  const isValidWalletType =
    selectedLoyaltyProgram && availableWallets.includes(selectedLoyaltyProgram);

  // Show WalletNotAvailable when user is logged in with costapass/travelpass but wallet is not available for route
  const showWalletNotAvailable =
    costapass.get('logged') &&
    !availableWallets.includes('costapass') &&
    !availableWallets.includes('travelpass');

  return {
    user: userData,
    isLogged: siemprePlus.get('logged') || costapass.get('logged'),
    showWalletNotAvailable,
    cardsUpdated: cardsJs.isUpdated,
    cardsLoading: cardsJs.isLoading,
    selectedCard,
    purchase: purchaseJs,
    isUpdating: purchase.get('isUpdating'),
    isExchange: purchase.get('isExchange'),
    fetchingPayment: payment.get('isFetching') || false,
    stripeSubmitting: payment.get('stripeSubmitting') || false,
    paymentId: payment.get('id'),
    cardErrorOccured: payment.get('cardErrorOccured'),
    purchaserUpdated: VALIDATE_PURCHASER ? isPurchaserValid : true,
    isPurchaserFormValid,
    purchaser: purchaserFields,
    firstPassengerProfile,
    cardValues: cardSelector(
      state,
      'holderName',
      'cardNumber',
      'cvv2',
      'expirationMonth',
      'expirationYear',
      'expirationDate',
      'paymentPlan',
      'monthlySelectedPlan',
      'billingAddress',
      'cardBrand',
    ),
    insuranceMessage: purchase.getIn(['config', 'insuranceMessage']),
    insuranceAmount: purchase.getIn(['config', 'insuranceAmount']),
    insuranceAmountWithTaxes: purchase.getIn(['config', 'insuranceAmountWithTaxes']),
    insuranceAmountIsUnitPrice: purchase.getIn(['config', 'unitAmount']),
    selectedPlan: purchase.get('selectedPlan'),
    paymentEngine: purchase.get('paymentEngine'),
    monthlySelectedPlan: purchase.get('monthlySelectedPlan'),
    selectedPaymentMethod: purchase.get('selectedPaymentMethod'),

    /**
     * Carbon ancillary props
     */
    carbonOffset: purchase.getIn(['config', 'carbonOffset'])?.toJS(),
    showRedeemModal: payment.get('showRedeemModal'),
    loadingOTP: payment.get('loadingOTP'),
    showOTPModal: payment.get('showOTPModal'),
    otpConfirmed: payment.get('otpConfirmed'),
    otpTriggerPayment: payment.get('otpTriggerPayment'),
    nitCodeValidated: payment.get('nitCodeValidated'),
    selectedLoyaltyProgram: isValidWalletType ? selectedLoyaltyProgram : null,
    isUserAuthenticated: !!(userData || reserhubAccount?.toJS()?.user),
    features,
    enableThreeDS: env.cielo?.enableThreeDS,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  fetchCardList: () => dispatch(fetchPaymentCards()),
  onToggleWantsInsurance: (token, wants) => dispatch(toggleWantsInsurance(token, wants)),
  onSubmitForm: (formName) => dispatch(submitForm(formName)),
  onFormFail: () =>
    dispatch(
      setError(null, 'check_the_fields_in_red', 'warning', false, null, null, null, 'fixed'),
    ),
  onNoCardSelected: () =>
    dispatch(setError(200, 'select_a_card', 'warning', false, null, null, 'fixed')),
  onPaymentAttempt: (token, type) => dispatch(paymentAttempt(token, type)),
  onThirdpartyPaymentAttempt: (token, type) => dispatch(thirdPartyPaymentAttempt(token, type)),
  onSelectPaymentOption: (option, method) => {
    dispatch(selectPaymentOption(option));
    dispatch(selectPaymentMethod(method || {}));
  },
  transferPaymentAttempt: (config) => dispatch(transferPaymentAttempt(config)),
  resetInstallmentsPlan: () => {
    dispatch(resetInstallmentsPlan());
  },
  storePaymentAttempt: (config) => dispatch(storePaymentAttempt(config)),
  transitionTo: (route) => ownProps.history.push(route),
  returnTo: (route) => ownProps.history.replace(route),
  setPurchaser: (token, fields) => dispatch(updatePurchaseFields(token, fields)),
  resetPaymentCardError: () => dispatch(resetPaymentCardError()),
  onEfectyPaymentAttempt: () => dispatch(efectyPaymentAttempt()),
  onBNPLPaymentAttempt: () => dispatch(bnplPaymentAttempt()),
  onWalletPaymentAttempt: () => dispatch(walletPaymentAttempt()),
  yapePaymentAttempt: (yapeFormData) => dispatch(yapePaymentAttempt(yapeFormData)),
  onEvertecPaymentAttempt: (paymentData) => dispatch(evertecPaymentAttempt(paymentData)),
  onConfirmEvertecPayment: () => dispatch(confirmEvertecPayment()),
  onEvertecPaymentFailure: () =>
    dispatch(setError(301, 'error_generating_payment', 'warning', false)),
  onYunoPaymentAttempt: (paymentData) => dispatch(yunoPaymentAttempt(paymentData)),
  fetchYunoData: (purchaseData) => dispatch(fetchYunoData(purchaseData)),
  clearError: () => dispatch(clearError()),
  /**
   * Carbon ancillary props
   */
  onToggleWantsCarbonOffset: (token, wants) => dispatch(toggleWantsCarbonOffset(token, wants)),
  clearOTP: () => dispatch(clearOTP()),
  generateOTP: (walletBalanceUsed) => dispatch(generateOTP(walletBalanceUsed)),
  dispatchCardPaymentAttempt: (token, cardValues, paymentInfo) =>
    dispatch(cardPaymentAttempt(token, cardValues, paymentInfo)),
  saveBulkPassengers: (token, passengerPayload) =>
    dispatch(saveBulkPassengers(token, passengerPayload)),
  savePassengers: (token, passengers) => dispatch(setPassengers(passengers, token)),
  redeemModalToggle: (show) => dispatch(redeemModalToggle(show)),
  onPaypalPaymentAttempt: (paymentType, options) =>
    dispatch(paypalPaymentAttempt(paymentType, options)),
});

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  onCardPaymentAttempt: (token, cardValues, paymentInfo) => {
    const { dispatchCardPaymentAttempt } = dispatchProps;
    const { monthlySelectedPlan } = stateProps.purchase;
    const cardValueWithMonthlyPlan = {
      ...cardValues,
      monthlySelectedPlan,
    };
    dispatchCardPaymentAttempt(token, cardValueWithMonthlyPlan, paymentInfo);
  },
  onTransferPaymentAttempt: ({ bank, personType, isLegacyPix, addressAttributes }) => {
    const {
      description,
      documentId,
      documentType,
      email,
      selectedInstallmentsPlan,
      token,
      total,
      selectedPaymentMethod,
      phone,
      phoneCode,
      phoneCountry,
    } = stateProps.purchase;
    const { transferPaymentAttempt } = dispatchProps;
    const { engine, provider } = selectedPaymentMethod;
    const isPSEMercadoPago = engine === 'mercadopago' && provider === 'pse';

    const validatedTotal =
      selectedInstallmentsPlan && selectedInstallmentsPlan.months > 1
        ? selectedInstallmentsPlan.total
        : total;

    const brand2ProcessorMap = getBrand2ProcessorMap();
    transferPaymentAttempt({
      bankId: bank,
      personType,
      documentType: brand2ProcessorMap[documentType],
      documentNumber: documentId,
      email,
      paymentDescription: description,
      purchaseToken: token,
      total: validatedTotal,
      engine: isLegacyPix ? availablePaymentEngines.mercadoPago : selectedPaymentMethod.engine,
      provider: isLegacyPix ? 'pix' : selectedPaymentMethod.provider,
      addressAttributes: isPSEMercadoPago
        ? {
            ...addressAttributes,
            phone,
            phoneCode,
            phoneCountry,
          }
        : {},
    });
  },
  onSetPurchaser: () => {
    const { setPurchaser } = dispatchProps;
    const { purchase, purchaser } = stateProps;

    return setPurchaser(purchase.token, purchaser);
  },
  onStorePaymentAttempt: () => {
    const {
      token,
      purchaserFirstName,
      purchaserLastName,
      documentId,
      documentType,
      selectedInstallmentsPlan,
      description,
      total,
    } = stateProps.purchase;
    const { storePaymentAttempt } = dispatchProps;

    const validatedTotal =
      selectedInstallmentsPlan && selectedInstallmentsPlan.months > 1
        ? selectedInstallmentsPlan.total
        : total;

    const brand2ProcessorMap = getBrand2ProcessorMap();
    storePaymentAttempt({
      token,
      documentType: brand2ProcessorMap[documentType],
      name: purchaserFirstName,
      lastName: purchaserLastName,
      identification: documentId,
      totalAmount: validatedTotal,
      currency: 'COP',
      description,
    });
  },

  // TODO: Review if this work for saved cards
  onAdyenPaymentAttempt: (selectedCard) => {
    const { token: purchaseToken, total, monthlySelectedPlan } = stateProps.purchase;
    const { currency } = stateProps.env;
    const { recurringDetail } = selectedCard;

    // This object don't have adyenToken because is not a new card
    // and only need the reference to make the payment
    const paymentInfo = {
      amount: total,
      reference: recurringDetail.recurringDetailReference,
      currency,
    };

    const cardValues = {
      holderName: recurringDetail.card.holderName,
      cardNumber: `${recurringDetail.additionalData.cardBin}xxxxxx${recurringDetail.card.number}`,
      cardBrand: recurringDetail.variant,
      monthlySelectedPlan,
    };

    cardPaymentAttempt(purchaseToken, cardValues, paymentInfo);
  },
});

const CheckoutContainer = connect(mapStateToProps, mapDispatchToProps, mergeProps)(Checkout);

const CheckoutWithPaymentProviders = (routerProps) => {
  // Reserhub unified account suppresses the Doters banner (LoyaltyWidget returns null when enabled).
  // Pass it down so the "additional services" gate doesn't render an empty section title.
  const { enabled: reserhubAccountEnabled } = useReserhubAccount();

  return (
    <CieloTDCProvider>
      <CheckoutContainer {...routerProps} reserhubAccountEnabled={reserhubAccountEnabled} />
    </CieloTDCProvider>
  );
};

export default CheckoutWithPaymentProviders;
